import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api, { formatINR } from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";

export default function LoanAgreement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);

  useEffect(() => { api.get(`/applications/${id}`).then(r => setApp(r.data)); }, [id]);

  if (!app) return <div className="p-10 text-slate-500">Loading…</div>;
  const agreement = app.agreement || {};
  const kfs = agreement.kfs || {};
  const offer = app.loan_offer || {};
  const b = app.borrower || {};
  const v = app.vehicle || {};

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="loan-agreement-page">
      <div className="max-w-4xl mx-auto no-print flex justify-between items-center mb-4">
        <Button variant="outline" onClick={() => navigate(-1)}><ArrowLeft size={16} className="mr-1" /> Back</Button>
        <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => window.print()} data-testid="print-agreement-btn"><Printer size={16} className="mr-1" /> Download PDF / Print</Button>
      </div>

      {/* ============ KEY FACT STATEMENT ============ */}
      <div className="sanction-letter mb-8" data-testid="kfs-section">
        <div className="border-b border-slate-300 pb-6 mb-6 flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold">Red Fort Capital Finance NBFC</div>
            <h1 className="text-3xl font-semibold mt-1">Key Fact Statement (KFS)</h1>
            <div className="text-sm text-slate-600 mt-2">As per RBI digital lending guidelines</div>
          </div>
          <div className="text-right text-sm">
            <div><span className="text-slate-500">KFS No: </span><span className="font-mono">{agreement.kfs_no || "—"}</span></div>
            <div><span className="text-slate-500">Date: </span><span className="font-mono">{(agreement.generated_at || "").slice(0, 10)}</span></div>
            <div><span className="text-slate-500">Application: </span><span className="font-mono">{app.application_id}</span></div>
          </div>
        </div>
        <table className="w-full text-sm border border-slate-300">
          <tbody>
            <Row l="1. Loan amount sanctioned" v={formatINR(kfs.loan_amount)} />
            <Row l="2. Contracted rate of interest (p.a., reducing balance)" v={kfs.roi ? `${kfs.roi}%` : "—"} />
            <Row l="3. Annual Percentage Rate (APR, incl. processing fee)" v={kfs.apr ? `${kfs.apr}%` : "—"} highlight />
            <Row l="4. Tenure" v={`${kfs.tenure_months || "—"} months`} />
            <Row l="5. Monthly EMI" v={formatINR(kfs.emi)} />
            <Row l="6. Processing fee (one-time, upfront)" v={formatINR(kfs.processing_fee)} />
            <Row l="7. Total interest payable over tenure" v={formatINR(kfs.total_interest)} />
            <Row l="8. Total amount payable (principal + interest)" v={formatINR(kfs.total_payable)} highlight />
            <Row l="9. Penal charges" v={kfs.penal_charge_note} />
            <Row l="10. Foreclosure terms" v={kfs.foreclosure_note} />
            <Row l="11. Grievance redressal" v={kfs.grievance_email} />
          </tbody>
        </table>
        <p className="text-xs text-slate-500 mt-4">No charges other than those listed above are payable by the borrower. Recovery practices follow the RFC Fair Practices Code and RBI collection conduct norms.</p>
      </div>

      {/* ============ LOAN AGREEMENT ============ */}
      <div className="sanction-letter" data-testid="agreement-section">
        <div className="border-b border-slate-300 pb-6 mb-6 flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold">Red Fort Capital Finance NBFC</div>
            <h1 className="text-3xl font-semibold mt-1">Used Car Loan Agreement</h1>
          </div>
          <div className="text-right text-sm">
            <div><span className="text-slate-500">Agreement No: </span><span className="font-mono">{agreement.agreement_no || "—"}</span></div>
            <div><span className="text-slate-500">Date: </span><span className="font-mono">{(agreement.generated_at || "").slice(0, 10)}</span></div>
          </div>
        </div>

        <p className="text-sm leading-relaxed mb-5">
          This Loan Agreement is executed between <strong>Red Fort Capital Finance NBFC</strong> ("Lender") and
          <strong> {b.full_name || "the Borrower"}</strong>, PAN <span className="font-mono">{b.pan || "—"}</span>,
          residing at {b.current_address || b.city || "—"} ("Borrower"), for the purchase of the vehicle described below.
        </p>

        <Section title="Vehicle (Hypothecated Security)">
          <Two l="Make / Model / Variant" v={`${v.make || ""} ${v.model || ""} ${v.variant || ""}`} />
          <Two l="Year / Fuel" v={`${v.year || ""} · ${v.fuel_type || ""}`} />
          <Two l="Registration / VIN" v={`${v.registration_number || "—"} · ${v.vin || "—"}`} />
          <Two l="Odometer" v={`${v.km_driven || 0} km`} />
        </Section>

        <Section title="Loan Terms">
          <Two l="Loan amount" v={formatINR(offer.loan_amount)} />
          <Two l="ROI (p.a.)" v={offer.roi ? `${offer.roi}%` : "—"} />
          <Two l="Tenure" v={`${offer.tenure_months || "—"} months`} />
          <Two l="EMI" v={formatINR(offer.emi)} />
          <Two l="Processing fee" v={formatINR(offer.processing_fee)} />
          <Two l="Down payment" v={formatINR(offer.down_payment)} />
        </Section>

        <div className="mt-5">
          <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold border-b border-slate-200 pb-1 mb-3">Key Covenants</div>
          <ol className="list-decimal pl-5 text-sm space-y-1.5 leading-relaxed">
            <li>The vehicle stands hypothecated to the Lender until full repayment; RC shall carry the hypothecation endorsement (Form 34).</li>
            <li>The Borrower shall maintain comprehensive insurance with the Lender as loss payee throughout the tenure.</li>
            <li>EMIs are payable monthly via eNACH mandate; dishonour attracts penal charges as per the KFS.</li>
            <li>The Borrower shall not sell, lease or further encumber the vehicle without the Lender's written consent.</li>
            <li>On default beyond 90 days, the Lender may recall the loan and repossess the vehicle following due process and RBI collection conduct norms.</li>
            <li>On full repayment, the Lender shall issue an NOC for hypothecation removal within 15 working days.</li>
            <li>Disputes are subject to arbitration; courts at the Lender's registered office city have jurisdiction.</li>
            {(offer.conditions || []).map((c, i) => <li key={`cond-${i}`}>{c}</li>)}
          </ol>
        </div>

        <div className="mt-12 grid grid-cols-2 gap-8 text-sm">
          <div>
            <div className="border-t border-slate-300 pt-2 w-56">Borrower Signature</div>
            <div className="text-xs text-slate-500 mt-1">{b.full_name || "Borrower"} · PAN {b.pan || "—"}</div>
          </div>
          <div className="text-right">
            <div className="border-t border-slate-300 pt-2 w-56 ml-auto">Authorised Signatory</div>
            <div className="text-xs text-slate-500 mt-1">{agreement.by_name || "—"} · Red Fort Capital Finance NBFC</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ l, v, highlight }) {
  return (
    <tr className={`border-b border-slate-200 ${highlight ? "bg-teal-50" : ""}`}>
      <td className="px-3 py-2 text-slate-600 w-2/3">{l}</td>
      <td className="px-3 py-2 font-mono text-slate-900">{v || "—"}</td>
    </tr>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-5">
      <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold border-b border-slate-200 pb-1 mb-3">{title}</div>
      <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">{children}</div>
    </div>
  );
}

function Two({ l, v }) {
  return (
    <div className="flex justify-between gap-3">
      <div className="text-slate-500">{l}</div>
      <div className="font-mono text-slate-900 text-right">{v || "—"}</div>
    </div>
  );
}
